/**
 * Lecture des champs numériques saisis en texte dans les formulaires logistiques.
 *
 * Une saisie illisible (« abc ») n'est JAMAIS convertie en « non renseigné » : elle rend un
 * résultat en échec, que le formulaire affiche sous le champ et qui bloque l'envoi — sans quoi la
 * clé serait silencieusement omise du corps et l'utilisateur croirait avoir saisi une valeur.
 */

import {
  LOGISTICS_INTEGER_MAX,
  logisticsAtMostMessage,
} from '@gozoki/api-types/src/validation/logistics'
import { euroToCents, formatCentsAsDecimal, formatCentsAsEuros } from '@gozoki/tools'
import { z } from 'zod'
import type { SimulationDetailActiveScenario } from '~/lib/logistics-simulations-query'

export const NUMERIC_FIELD_ERROR = 'Valeur numérique invalide'

/** Montant en euros, « — » s'il n'est pas chiffrable (ou absent d'un ancien scénario). */
export const formatCents = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '—' : formatCentsAsEuros(cents)

/** Chiffrage d'un scénario (`costBreakdown` du solveur) et qualité de ses distances. */
export type ScenarioCost = Pick<SimulationDetailActiveScenario, 'costBreakdown' | 'dataQuality'>

type CostItem = ScenarioCost['costBreakdown']['unknownItems'][number]

/** Postes hors péages : le `Record` oblige à y ranger tout poste ajouté au solveur. */
const ITEMS_EXCLUDING_TOLLS: Record<Exclude<CostItem, 'toll'>, true> = {
  vehicle: true,
  fuel: true,
  hub: true,
}

/**
 * Coût d'un scénario dont seuls les péages manquent : distances mesurées par le routeur, appel
 * péages en échec (l'arc n'est pas mis en cache, le calcul suivant les redemande). `null` dans tout
 * autre cas : total connu, ou distances estimées, dont Léo ne veut pas de coût au centime (01/10).
 */
export const costExcludingTollsCents = ({ costBreakdown, dataQuality }: ScenarioCost) => {
  const { unknownItems } = costBreakdown
  const onlyTollsUnknown = unknownItems.length === 1 && unknownItems[0] === 'toll'
  if (dataQuality !== 'measured' || !onlyTollsUnknown) return null

  const items = Object.keys(ITEMS_EXCLUDING_TOLLS) as Exclude<CostItem, 'toll'>[]
  // Carburant absent des runs postérieurs à LU-160 (compris dans le coût au km du camion).
  const cents = items.flatMap((item) => {
    const value = costBreakdown[`${item}Cents`]
    return value === undefined ? [] : [value]
  })
  const known = cents.filter((value): value is number => value !== null)
  // Montant à `null` que `unknownItems` ne nomme pas : chiffrage incohérent, rien n'est affiché.
  if (known.length !== cents.length) return null
  return known.reduce((sum, value) => sum + value, 0)
}

/** Coût par jour d'un scénario : son total, sinon « 110,41 € hors péages », sinon « — ». */
export const formatScenarioCost = (cost: ScenarioCost): string => {
  const excludingTolls = costExcludingTollsCents(cost)
  return excludingTolls === null
    ? formatCents(cost.costBreakdown.totalCents)
    : `${formatCentsAsEuros(excludingTolls)} hors péages`
}

export type ParsedNumber = { ok: true; value: number | undefined } | { ok: false }

/**
 * Champ vide = non renseigné (`value: undefined`) : la clé sera omise du corps, jamais envoyée à
 * `null` — plusieurs champs optionnels du backend n'acceptent pas un `null` explicite. La virgule
 * décimale française est acceptée (`12,5` vaut `12.5`).
 */
export const optionalNumber = (value: string): ParsedNumber => {
  const trimmed = value.trim()
  if (trimmed === '') return { ok: true, value: undefined }

  const parsed = Number(trimmed.replace(',', '.'))
  return Number.isFinite(parsed) ? { ok: true, value: parsed } : { ok: false }
}

export type ParsedNumericFields<K extends string> =
  | { ok: true; values: Record<K, number | undefined> }
  | { ok: false; invalid: K[] }

/**
 * Lit d'un coup les champs numériques d'un formulaire : soit toutes les valeurs (clés inchangées,
 * prêtes à être étalées dans le corps de la mutation), soit la liste des champs illisibles.
 */
export const parseNumericFields = <K extends string>(
  fields: Record<K, string>,
): ParsedNumericFields<K> => {
  const values = {} as Record<K, number | undefined>
  const invalid: K[] = []

  for (const key of Object.keys(fields) as K[]) {
    const parsed = optionalNumber(fields[key])
    if (parsed.ok) values[key] = parsed.value
    else invalid.push(key)
  }

  return invalid.length === 0 ? { ok: true, values } : { ok: false, invalid }
}

/**
 * Rattachement atelier/hub : ici le `null` est requis (exactement une des deux clés est remplie),
 * et la valeur vient d'un `<select>` — jamais d'une saisie libre, donc jamais illisible.
 */
export const numberOrNull = (value: string): number | null => {
  const parsed = optionalNumber(value)
  return parsed.ok ? (parsed.value ?? null) : null
}

/** Valeur d'un champ texte pour un nombre lu (absent = champ vide). */
export const numberToField = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

/** Nombre d'un champ déjà validé par le schéma (vide = `undefined`). */
export const numberOf = (value: string): number | undefined => {
  const parsed = optionalNumber(value)
  return parsed.ok ? parsed.value : undefined
}

/** Contraintes d'un nombre saisi : celles du validateur serveur du même champ. */
export interface NumberRule {
  integer?: boolean
  positive?: boolean
  /** Saisie requise (vide refusé). */
  required?: boolean
  /** Borne haute incluse, dans l'unité saisie. */
  max?: number
  /** Décimales admises (l'affichage relu n'en garde pas plus). */
  decimals?: number
}

/** Message d'erreur d'un nombre saisi (vide = non renseigné, valide), `null` s'il convient. */
export const numberFieldIssue = (value: string, rule: NumberRule): string | null => {
  const parsed = optionalNumber(value)
  if (!parsed.ok) return NUMERIC_FIELD_ERROR
  if (parsed.value === undefined) return rule.required ? 'Valeur requise.' : null
  if (rule.integer && !Number.isInteger(parsed.value)) return 'Nombre entier attendu.'
  if (rule.positive && parsed.value <= 0) return 'Nombre strictement positif attendu.'
  if (!rule.positive && parsed.value < 0) return 'Nombre positif ou nul attendu.'
  if (rule.decimals !== undefined) {
    const factor = 10 ** rule.decimals
    if (Math.round(parsed.value * factor) / factor !== parsed.value) {
      return `${rule.decimals} décimales au plus.`
    }
  }
  if (rule.max !== undefined && parsed.value > rule.max) {
    return logisticsAtMostMessage(rule.max)
  }
  return null
}

/** Champ texte d'un nombre, validé par `numberFieldIssue`. */
export const numberField = (rule: NumberRule) =>
  z.string().superRefine((value, context) => {
    const issue = numberFieldIssue(value, rule)
    if (issue !== null) context.addIssue({ code: 'custom', message: issue })
  })

/**
 * Entier > 0 d'une fiche, jamais 0 (non renseigné), au plus la capacité d'une colonne `integer` :
 * mêmes bornes que les validateurs des référentiels logistiques.
 */
export const positiveIntegerField = () =>
  numberField({ integer: true, positive: true, max: LOGISTICS_INTEGER_MAX })

/** Entier ≥ 0 d'une fiche (0 admis), au plus la capacité d'une colonne `integer`. */
export const NON_NEGATIVE_INTEGER_RULE: NumberRule = { integer: true, max: LOGISTICS_INTEGER_MAX }

/**
 * Montant saisi en euros (deux décimales), borné par les centimes stockés (message sans le nombre,
 * illisible). `positive` : zéro refusé.
 */
export const eurosField = ({ positive = false }: { positive?: boolean } = {}) =>
  numberField({ positive, decimals: 2 }).refine(
    (euros) => euroToCents(numberOf(euros) ?? 0) <= LOGISTICS_INTEGER_MAX,
    'Montant trop élevé.',
  )

/** Montant en centimes pré-rempli dans un champ en euros, sans séparateur de milliers. */
export const centsToField = (cents: number | null) =>
  cents === null ? '' : formatCentsAsDecimal(cents)

/** Montant saisi en euros → centimes, `null` si vide. */
export const centsOfField = (euros: string) => {
  const value = numberOf(euros)
  return value === undefined ? null : euroToCents(value)
}
